import type { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query'
import { createApi } from '@reduxjs/toolkit/query/react'

import { HTTP_METHODS } from 'common/constants/httpMethodsConstants'
import { baseQuery } from 'common/store/api/fetchBaseQuery'
import { REDUCERS } from 'common/store/reducerConstants'

import {
  transformChiefComplaintSet,
  transformComplaintSuggestions,
  toComplaintQuery,
} from './chiefComplaints.transform'
import type {
  ChiefComplaintSet,
  ComplaintSuggestions,
  ReplaceChiefComplaintsBody,
} from './chiefComplaints.types'
import { siteHeaders } from './emrHeaders'

export const CHIEF_COMPLAINTS_TAG = 'AppointmentChiefComplaints'

type BookingArgs = { appointmentId: number; siteId: string }

// A failed load, save or search must answer now, not after the base query's minute of retries.
const NO_RETRY = { maxRetries: 0 }

export const chiefComplaintsApi = createApi({
  reducerPath: REDUCERS.CHIEF_COMPLAINTS,
  baseQuery: baseQuery(),
  tagTypes: [CHIEF_COMPLAINTS_TAG],
  endpoints: (build: EndpointBuilder<BaseQueryFn, string, string>) => ({
    fetchAppointmentChiefComplaints: build.query<ChiefComplaintSet, BookingArgs>({
      query: ({ appointmentId, siteId }) => ({
        url: `/emr/appointment/${appointmentId}/chief-complaints`,
        headers: siteHeaders(siteId),
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformChiefComplaintSet,
      providesTags: (_result, _error, { appointmentId }) => [
        { type: CHIEF_COMPLAINTS_TAG, id: appointmentId },
      ],
    }),

    replaceAppointmentChiefComplaints: build.mutation<
      ChiefComplaintSet,
      BookingArgs & { data: ReplaceChiefComplaintsBody }
    >({
      query: ({ appointmentId, siteId, data }) => ({
        url: `/emr/appointment/${appointmentId}/chief-complaints`,
        method: HTTP_METHODS.PUT,
        headers: siteHeaders(siteId),
        data,
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformChiefComplaintSet,
      // The answer is the saved list, so the booking's cache takes it as is.
      async onQueryStarted({ appointmentId, siteId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            chiefComplaintsApi.util.upsertQueryData(
              'fetchAppointmentChiefComplaints',
              { appointmentId, siteId },
              data,
            ),
          )
        } catch {
          // The save hook reports the failure.
        }
      },
    }),

    searchChiefComplaints: build.query<ComplaintSuggestions, { patientId: number; q: string }>({
      query: ({ patientId, q }) => ({
        url: `/emr/patient/${patientId}/chief-complaints`,
        params: { q: toComplaintQuery(q) },
      }),
      extraOptions: NO_RETRY,
      transformResponse: transformComplaintSuggestions,
    }),
  }),
})

export const {
  useFetchAppointmentChiefComplaintsQuery,
  useReplaceAppointmentChiefComplaintsMutation,
  useSearchChiefComplaintsQuery,
} = chiefComplaintsApi
